import { useEffect, useState } from 'react';
import { Layout, Menu, Typography, Button } from 'antd';
import {
  FileTextOutlined,
  PictureOutlined,
  TrophyOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { SITE } from '@nagoya-cat/shared';
import { can } from '../auth/useAuth';
import HeaderUserMenu from './HeaderUserMenu';
import type { Me } from '../api/client';

const { Sider, Header, Content } = Layout;

interface Props {
  me: Me;
  children: React.ReactNode;
}

/** 後台外殼：白色側欄 + 站名 + 權限選單。 */
export default function AppLayout({ me, children }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sider-collapsed') === '1');

  useEffect(() => {
    localStorage.setItem('sider-collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  const items = [
    { key: '/articles', icon: <FileTextOutlined />, label: '文章與頁面' },
    { key: '/media', icon: <PictureOutlined />, label: '媒體庫' },
    { key: '/contests', icon: <TrophyOutlined />, label: '比賽存檔' },
    can(me, 'user.manage') && { key: '/users', icon: <TeamOutlined />, label: '帳號管理' },
  ].filter(Boolean) as { key: string; icon: React.ReactNode; label: string }[];

  const selected = '/' + (location.pathname.split('/')[1] || 'articles');

  return (
    <Layout className="min-h-screen">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        onBreakpoint={(broken) => broken && setCollapsed(true)}
        collapsedWidth={64}
        width={216}
        className="border-r border-gray-100"
      >
        <div className={`flex h-16 items-center overflow-hidden ${collapsed ? 'justify-center px-2' : 'gap-2 px-5'}`}>
          <span className="truncate text-base font-bold text-[#dd7900]">
            {collapsed ? '喵' : SITE.name}
          </span>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selected]}
          items={items}
          onClick={({ key }) => navigate(key)}
          className="!border-e-0"
        />
      </Sider>
      <Layout>
        <Header className="flex items-center justify-between border-b border-gray-100 px-4">
          <div className="flex items-center gap-2">
            <Button
              type="text"
              aria-label="收折側欄"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((c) => !c)}
            />
            <Typography.Text strong className="text-base">
              {items.find((i) => i.key === selected)?.label ?? '後台'}
            </Typography.Text>
          </div>
          <HeaderUserMenu me={me} />
        </Header>
        <Content className="p-6">
          <div className="mx-auto max-w-6xl">{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
}
